import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ClientMenuItemDto, ClientMenuItemVariantDto } from '../api/restApi';

export interface VariantWithQuantity {
  quantity: number;
  value: ClientMenuItemVariantDto;
}

export interface MenuItemWithQuantity {
  quantity: number;
  product: ClientMenuItemDto;
  variants?: VariantWithQuantity[];
}

export interface BasketState {
  totalCheck: number;
  numberOfProducts: number;
  products: MenuItemWithQuantity[];
  unavailableProductIds: string[];
}

const initialState: BasketState = {
  products: [],
  totalCheck: 0,
  numberOfProducts: 0,
  unavailableProductIds: [],
};

const isProductAvailable = (state: BasketState, productId: string) => !state.unavailableProductIds.includes(productId);

export const basketSlice = createSlice({
  name: 'basket',

  initialState,

  reducers: {
    addToBasket(
      state,
      {
        payload: { product, quantity, variant },
      }: PayloadAction<{ product: ClientMenuItemDto; quantity: number; variant?: ClientMenuItemVariantDto }>,
    ) {
      const productInBasket = state.products.find((prd) => prd.product.id === product.id);
      const shouldUpdateTotalCheck = isProductAvailable(state, product.id);

      if (variant) {
        if (productInBasket) {
          const variantInBasket = productInBasket.variants?.find((vrnt) => vrnt.value.id === variant.id);

          if (variantInBasket) {
            const quantityDifference = quantity - variantInBasket.quantity;
            state.numberOfProducts += quantityDifference;
            if (shouldUpdateTotalCheck) {
              state.totalCheck = +(state.totalCheck + (variant.price ?? 0) * quantityDifference).toFixed(2);
            }

            productInBasket.quantity += quantityDifference;
            variantInBasket.quantity = quantity;
          } else {
            state.numberOfProducts += quantity;
            if (shouldUpdateTotalCheck) {
              state.totalCheck = +(state.totalCheck + (variant.price ?? 0) * quantity).toFixed(2);
            }

            productInBasket.quantity += quantity;
            productInBasket.variants = [...(productInBasket.variants ?? []), { value: variant, quantity }];
          }
        } else {
          state.numberOfProducts += quantity;
          if (shouldUpdateTotalCheck) {
            state.totalCheck = +(state.totalCheck + (variant.price ?? 0) * quantity).toFixed(2);
          }
          state.products = [...state.products, { product, quantity, variants: [{ value: variant, quantity }] }];
        }
      } else {
        if (productInBasket) {
          const quantityDifference = quantity - productInBasket.quantity;
          state.numberOfProducts += quantityDifference;
          if (shouldUpdateTotalCheck) {
            state.totalCheck = +(state.totalCheck + (product.price ?? 0) * quantityDifference).toFixed(2);
          }

          productInBasket.quantity = quantity;
        } else {
          state.numberOfProducts += quantity;
          state.products = [...state.products, { product, quantity }];
          if (shouldUpdateTotalCheck) {
            state.totalCheck = +(state.totalCheck + (product.price ?? 0) * quantity).toFixed(2);
          }
        }
      }
    },

    removeFromBasket(
      state,
      { payload: { productId, variantId } }: PayloadAction<{ productId: string; variantId?: string }>,
    ) {
      const productInBasket = state.products.find((elem) => elem.product.id === productId);
      if (!productInBasket) return;
      const shouldUpdateTotalCheck = isProductAvailable(state, productId);

      if (variantId && productInBasket.variants) {
        const variantInBasket = productInBasket.variants.find((vrnt) => vrnt.value.id === variantId);
        if (!variantInBasket) return;

        state.numberOfProducts -= variantInBasket.quantity;
        if (shouldUpdateTotalCheck) {
          state.totalCheck = +(
            state.totalCheck - variantInBasket.quantity * (variantInBasket.value.price ?? 0)
          ).toFixed(2);
        }

        if (productInBasket.variants.length > 1) {
          productInBasket.quantity -= variantInBasket.quantity;
          productInBasket.variants = productInBasket.variants?.filter((vrnt) => vrnt.value.id !== variantId);
        } else {
          state.products = state.products.filter((elem) => elem.product.id !== productId);
          state.unavailableProductIds = state.unavailableProductIds.filter((id) => id !== productId);
        }
      } else {
        state.numberOfProducts -= productInBasket.quantity;
        if (shouldUpdateTotalCheck) {
          state.totalCheck = +(
            state.totalCheck -
            productInBasket.quantity * (productInBasket.product.price ?? 0)
          ).toFixed(2);
        }

        state.products = state.products.filter((elem) => elem.product.id !== productId);
        state.unavailableProductIds = state.unavailableProductIds.filter((id) => id !== productId);
      }
    },

    increaseItemCount(
      state,
      {
        payload: { product, variant },
      }: PayloadAction<{ product: ClientMenuItemDto; variant?: ClientMenuItemVariantDto }>,
    ) {
      const productInBasket = state.products.find((prd) => prd.product.id === product.id);
      const shouldUpdateTotalCheck = isProductAvailable(state, product.id);

      if (variant) {
        if (productInBasket) {
          productInBasket.quantity += 1;
          const variantInBasket = productInBasket.variants?.find((vrnt) => vrnt.value.id === variant.id);

          if (variantInBasket) variantInBasket.quantity += 1;
          else productInBasket.variants = [...(productInBasket.variants ?? []), { value: variant, quantity: 1 }];
        } else
          state.products = [...state.products, { product, quantity: 1, variants: [{ value: variant, quantity: 1 }] }];

        if (shouldUpdateTotalCheck) {
          state.totalCheck = +(state.totalCheck + (variant.price ?? 0)).toFixed(2);
        }
      } else {
        if (productInBasket) productInBasket.quantity += 1;
        else state.products = [...state.products, { product, quantity: 1 }];

        if (shouldUpdateTotalCheck) {
          state.totalCheck = +(state.totalCheck + (product.price ?? 0)).toFixed(2);
        }
      }

      state.numberOfProducts += 1;
    },

    decreaseItemCount(
      state,
      { payload: { productId, variantId } }: PayloadAction<{ productId: string; variantId?: string }>,
    ) {
      const productInBasket = state.products.find((prd) => prd.product.id === productId);

      if (!productInBasket) return;
      const shouldUpdateTotalCheck = isProductAvailable(state, productId);

      if (variantId && productInBasket.variants) {
        const variantInBasket = productInBasket.variants.find((vrnt) => vrnt.value.id === variantId);

        if (!variantInBasket) return;
        else {
          if (variantInBasket.quantity > 1) {
            variantInBasket.quantity -= 1;
            productInBasket.quantity -= 1;
          } else {
            if (productInBasket.variants.length > 1) {
              productInBasket.variants = productInBasket.variants.filter((vrnt) => vrnt.value.id !== variantId);
              productInBasket.quantity -= 1;
            } else {
              state.products = state.products.filter((prd) => prd.product.id !== productId);
              state.unavailableProductIds = state.unavailableProductIds.filter((id) => id !== productId);
            }
          }
        }

        if (shouldUpdateTotalCheck) {
          state.totalCheck = +(state.totalCheck - (variantInBasket.value.price ?? 0)).toFixed(2);
        }
      } else {
        if (productInBasket.quantity > 1) productInBasket.quantity -= 1;
        else {
          state.products = state.products.filter((elem) => elem.product.id !== productId);
          state.unavailableProductIds = state.unavailableProductIds.filter((id) => id !== productId);
        }

        if (shouldUpdateTotalCheck) {
          state.totalCheck = +(state.totalCheck - (productInBasket.product.price ?? 0)).toFixed(2);
        }
      }

      state.numberOfProducts -= 1;
    },

    changeProductPrice(state, { payload: { productId, price } }: PayloadAction<{ productId: string; price: number }>) {
      const productInBasket = state.products.find((elem) => elem.product.id === productId);

      if (productInBasket) {
        if (!state.unavailableProductIds.includes(productId)) {
          state.totalCheck = +(
            state.totalCheck +
            productInBasket.quantity * (price - (productInBasket.product.price ?? 0))
          ).toFixed(2);
        }

        productInBasket.product.price = price;
      }
    },

    changeTotalCheck(
      state,
      {
        payload: { productId, operation },
      }: PayloadAction<{ productId: string; operation: 'add' | 'subtract' }>,
    ) {
      const productInBasket = state.products.find((elem) => elem.product.id === productId);
      if (!productInBasket) return;

      const productTotalCheck = productInBasket.variants
        ? productInBasket.variants.reduce((sum, variant) => sum + variant.quantity * (variant.value.price ?? 0), 0)
        : productInBasket.quantity * (productInBasket.product.price ?? 0);

      state.totalCheck =
        operation === 'add'
          ? +(state.totalCheck + productTotalCheck).toFixed(2)
          : +(state.totalCheck - productTotalCheck).toFixed(2);
    },

    changeUnavailableProductIds(state, { payload }: PayloadAction<string[]>) {
      state.unavailableProductIds = payload;
    },

    clearBasket(state) {
      state.products = initialState.products;
      state.totalCheck = initialState.totalCheck;
      state.numberOfProducts = initialState.numberOfProducts;
      state.unavailableProductIds = initialState.unavailableProductIds;
    },
  },
});

export const {
  addToBasket,
  clearBasket,
  removeFromBasket,
  increaseItemCount,
  decreaseItemCount,
  changeProductPrice,
  changeTotalCheck,
  changeUnavailableProductIds,
} = basketSlice.actions;

export const basketReducer = basketSlice.reducer;
